import { useState, useEffect, useRef } from "react";
import styles from './NamePlaceThing.module.css'
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import imgHeader from '../../../images/Nameplacething-Header.jpg';
import imgLobby from '../../../images/nameplacething-1.png';
import imgGame from '../../../images/nameplacething-2.png';
import imgLeaderboard from '../../../images/nameplacething-3.png';
import imgCountdown from '../../../images/nameplacething-4.png';
import imgGamePage from '../../../images/nameplacething-5.png';
import imgScoring from '../../../images/nameplacething-6.png';
import imgWinner from '../../../images/nameplacething-8.png';

const FEATURES = [
  { icon: "🔥", cls: "fc-1", title: "Firebase Real-Time Sync", desc: "All game state — answers, scores, timers — synced via Firebase RTDB. No custom backend server needed." },
  { icon: "⏱️", cls: "fc-2", title: "Server-Authoritative Timer", desc: "Uses .info/serverTimeOffset to sync the 60s countdown across all clients regardless of local clock drift." },
  { icon: "🏆", cls: "fc-3", title: "First-Submit Bonus", desc: "+10 bonus points for the player who fills all 5 categories and submits first in a round." },
  { icon: "🎭", cls: "fc-4", title: "15 Random Avatars", desc: "Players pick from 15 animated avatars with a refresh button. Avatars persist across all pages via sessionStorage." },
  { icon: "🔤", cls: "fc-5", title: "Letter History Tracking", desc: "Firebase tracks used alphabets per game — no letter is repeated in the same session." },
  { icon: "👑", cls: "fc-6", title: "Host Controls", desc: "Crown indicator on host. Only the host can start rounds. Graceful handling if host disconnects mid-game." },
  { icon: "📊", cls: "fc-7", title: "Round-Robin Peer Scoring", desc: "Each player scores exactly the next player alphabetically. Fair, fast, and every answer gets scored by exactly one peer." },
  { icon: "✨", cls: "fc-8", title: "Cyberpunk Particle UI", desc: "Animated particles, parallax effects, glowing neon elements, and a scanline overlay built entirely in vanilla JS + CSS." },
];

const STACK = [
  { icon: "🌐", name: "HTML / CSS / JS", purpose: "Frontend — zero framework. Vanilla JS for all game logic, DOM manipulation, and Firebase interaction." },
  { icon: "🎨", name: "Bootstrap 5", purpose: "Responsive layout, modals, and utility classes. Customised heavily with CSS variables for the neon theme." },
  { icon: "🔥", name: "Firebase RTDB", purpose: "Real-time multiplayer sync. onValue listeners for game state, onDisconnect for host cleanup, serverTimeOffset for timer." },
  { icon: "📝", name: "Google Fonts", purpose: "Orbitron for gaming-style headings + Poppins for readable body text. Loaded via CDN." },
  { icon: "🔷", name: "Font Awesome + Bootstrap Icons", purpose: "Iconography throughout all 8 pages — categories, buttons, player indicators, status badges." },
  { icon: "▲", name: "Vercel", purpose: "Zero-config deployment from GitHub. Each commit triggers a new deploy. Preview URLs for every branch." },
];

const PAGES = [
  { file: "index.html", name: "Landing Page", desc: "Game intro, category badges, how-to-play instructions, and entry CTAs." },
  { file: "create-game.html", name: "Create Room", desc: "Host picks round count (5/7/10), selects avatar, generates unique room code." },
  { file: "join.html", name: "Join Room", desc: "Players enter nickname + room code to join an existing session." },
  { file: "player-home.html", name: "Lobby", desc: "Waiting room showing all connected players, avatars, and host crown indicator." },
  { file: "countdown.html", name: "Countdown", desc: "Full-screen animated 5→4→3→2→1→GO! before each round starts." },
  { file: "game-page.html", name: "Game Page", desc: "Main game: random alphabet, 5 input fields, live 60s timer, live scoreboard." },
  { file: "scoring.html", name: "Peer Scoring", desc: "0–10 sliders per category. Each player scores the next player's answers." },
  { file: "winner.html", name: "Winner Screen", desc: "Animated trophy, confetti explosion, and final leaderboard with all scores." },
];

const CHALLENGES = [
  { num: "01", problem: "Timer drift across clients", fix: "Used Firebase serverTimeOffset to sync countdown regardless of local clock differences.", code: "const offset = (await get(ref(db, '.info/serverTimeOffset'))).val();" },
  { num: "02", problem: "Host disconnect mid-game", fix: "Implemented onDisconnect cleanup so another player auto-becomes host.", code: "onDisconnect(hostRef).remove();" },
  { num: "03", problem: "Duplicate letter selection", fix: "Firebase tracks used alphabets per game session — no repeats.", code: "set(ref(db, `games/${code}/usedLetters/${letter}`), true);" },
  { num: "04", problem: "Race condition on submit", fix: "Server-side timestamp comparison ensures first-submit bonus goes to the right player.", code: "const ts = serverTimestamp();" },
  { num: "05", problem: "Peer scoring fairness", fix: "Round-robin assignment: each player scores exactly the next player alphabetically.", code: "const scorerIdx = (playerIdx + 1) % players.length;" },
  { num: "06", problem: "State persistence on refresh", fix: "sessionStorage for avatar + nickname; Firebase for game state.", code: "sessionStorage.setItem('avatar', selectedAvatar);" },
  { num: "07", problem: "Mobile keyboard covering inputs", fix: "ScrollIntoView on focus + viewport height adjustment for iOS.", code: "input.addEventListener('focus', () => input.scrollIntoView());" },
];

export default function NPATCaseStudy() {
  const revealRefs = useRef([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const r = (i) => el => { revealRefs.current[i] = el; };

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles['grid-bg']} />

    

      {/* HERO */}
      <div style={{ position: "relative", zIndex: 2 }}>
        <div className={styles.hero}>
          <div>
            <div className={styles['hero-eyebrow']}>
              <span className={styles['eyebrow-dot']} />
              Multiplayer Web Game — Case Study
            </div>
            <h1 className={styles['hero-title']}>
              <span className={styles.t1}>NAME.</span>
              <span className={styles.t2}>PLACE.</span>
              <span className={styles.t1}>ANIMAL.</span><br/>
              <span className={styles.t3}>THING.</span>
            </h1>
            <p className={styles['hero-tagline']}>// Real-time · 5 categories · 60 seconds · No backend</p>
            <p className={styles['hero-desc']}>
              The classic Indian word game rebuilt as a real-time multiplayer browser experience.
              Firebase RTDB powering live sync, peer scoring, and a fully animated cyberpunk UI —
              all in vanilla HTML, CSS, and JavaScript.
            </p>
            <div className={styles['hero-btns']}>
              <a href="#" className={`${styles['btn-neon']} ${styles.primary}`}>▶ Play Live Demo</a>
              {/* <a href="#" className={`${styles['btn-neon']} ${styles.ghost}`}>⎔ View GitHub</a> */}
            </div>
            <div className={styles['hero-meta']}>
              <div className={styles['meta-item']}><div className={styles.mlabel}>Stack</div><div className={styles.mval}>Vanilla JS + Firebase</div></div>
              <div className={styles['meta-item']}><div className={styles.mlabel}>Backend</div><div className={styles.mval}>None</div></div>
              <div className={styles['meta-item']}><div className={styles.mlabel}>Pages</div><div className={styles.mval}>8</div></div>
              <div className={styles['meta-item']}><div className={styles.mlabel}>Deploy</div><div className={styles.mval}>Vercel</div></div>
            </div>
          </div>

          {/* HERO IMAGE */}
          <div className={styles['hero-screen']}>
            <img src={require('../../../images/Nameplacething-Header.jpg')} alt="Name Place Animal Thing game screenshot" className={styles['hero-img']} />
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className={styles['stats-strip']}>
        {[
          ["8","HTML Pages"],["7","Bug Fixes"],["90K","Room Combinations"],
          ["5","Categories"],["60s","Round Timer"],["0","Backend Servers"]
        ].map(([v, l], i) => (
          <div key={i} className={styles['stat-block']}>
            <span className={styles['stat-num']}>{v}</span>
            <div className={styles['stat-lbl']}>{l}</div>
          </div>
        ))}
      </div>

      {/* HOW THE GAME WORKS */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(0)}>
            <div className={styles['sec-chip']}>The Game</div>
            <h2 className={styles['sec-heading']}>How it <span className={styles.a1}>plays</span></h2>
            <p className={styles['sec-sub']}>The full round loop — from room creation to winner screen — in seven steps.</p>
          </div>
          <div className={`${styles['how-flow']} ${styles.reveal}`} ref={r(1)}>
            {[
              { n: "01", icon: "🏠", title: "Create Room", desc: "Host picks rounds (5/7/10), selects avatar, gets a unique Food-Color-Number room code." },
              { n: "02", icon: "👥", title: "Players Join", desc: "Friends enter the room code and a nickname. No account needed." },
              { n: "03", icon: "⏳", title: "Countdown", desc: "Host starts game. A synced 5→1→GO! full-screen countdown fires for everyone." },
              { n: "04", icon: "🔤", title: "Random Letter", desc: "A random alphabet is revealed. Players have 60 seconds to fill Name, Place, Animal, Thing, Film." },
              { n: "05", icon: "⚡", title: "Submit & Bonus", desc: "First to submit all 5 gets +10 bonus. Submission triggers scoring phase for all." },
              { n: "06", icon: "📊", title: "Peer Score", desc: "Each player rates the next player's answers 0–10 per category via sliders." },
              { n: "07", icon: "🏆", title: "Winner", desc: "After all rounds: confetti, trophy animation, and the final leaderboard." },
            ].map((s, i) => (
              <div key={i} className={styles['how-step']} data-n={s.n}>
                <div className={styles.hnum}>{s.n}</div>
                <span className={styles.hicon}>{s.icon}</span>
                <div className={styles.htitle}>{s.title}</div>
                <p className={styles.hdesc}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECH STACK */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(5)}>
            <div className={styles['sec-chip']}>Tech Stack</div>
            <h2 className={styles['sec-heading']}>Built with <span className={styles.a2}>no framework</span> — deliberately</h2>
            <p className={styles['sec-sub']}>Every page is vanilla HTML, CSS, and JavaScript. The goal was deep understanding of the Firebase SDK and real-time patterns without framework abstractions.</p>
          </div>
          <div className={`${styles['tech-table']} ${styles.reveal}`} ref={r(6)}>
            <div className={styles['tech-row']}>
              <div className={`${styles['tech-cell']} ${styles.header}`}>Technology</div>
              <div className={`${styles['tech-cell']} ${styles.header}`}>Layer</div>
              <div className={`${styles['tech-cell']} ${styles.header} ${styles.purpose}`}>Why I chose it</div>
            </div>
            {STACK.map((s, i) => (
              <div key={i} className={styles['tech-row']}>
                <div className={styles['tech-cell']}><span className={styles['tech-icon']}>{s.icon}</span><span className={styles['tech-name']}>{s.name}</span></div>
                <div className={styles['tech-cell']} style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: "1.2rem",fontWeight:"100", color: "var(--neon3)" }}>
                  {["Frontend","UI Layer","Real-time DB","Typography","Icons","Deploy"][i]}
                </div>
                <div className={`${styles['tech-cell']} ${styles.purpose}`}><span className={styles['tech-purpose']}>{s.purpose}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8 PAGES */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(10)}>
            <div className={styles['sec-chip']}>Pages Built</div>
            <h2 className={styles['sec-heading']}><span className={styles.a1}>8 pages</span> — each with a distinct job</h2>
            <p className={styles['sec-sub']}>No single-page routing. Each page owns its own Firebase listeners, cleanup logic, and state transitions.</p>
          </div>
          <div className={`${styles['pages-grid']} ${styles.reveal}`} ref={r(11)}>
            {PAGES.map((p, i) => (
              <div key={i} className={styles['page-card']}>
                <div className={styles['page-file']}>{p.file}</div>
                <div className={styles['page-name']}>{p.name}</div>
                <p className={styles['page-desc']}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROOM CODE */}
      {/* <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(15)}>
            <div className={styles['sec-chip']}>Room Code System</div>
            <h2 className={styles['sec-heading']}>90,000 <span className={styles.a3}>unique rooms</span></h2>
          </div>
          <div className={`${styles['room-showcase']} ${styles.reveal}`} ref={r(16)}>
            <div className={styles['room-left']}>
              <div className={styles['room-title']}>Food · Color · Number format</div>
              <p className={styles['room-sub']}>
                Replaced simple numeric codes with a memorable three-part format.
                10 foods × 10 colors × 900 numbers = 90,000 combinations.
                Memorable to read aloud, impossible to guess accidentally.
              </p>
              <div style={{ marginTop: "1rem", fontFamily: "'Share Tech Mono', monospace", fontSize: "0.72rem", color: "var(--neon)", padding: "0.8rem 1rem", background: "rgba(0,245,255,0.04)", border: "1px solid rgba(0,245,255,0.15)", borderRadius: "4px", display: "inline-block" }}>
                10 × 10 × 900 = 90,000 unique room codes
              </div>
            </div>
            <div className={styles['room-codes']}>
              {[
                { icon: "🍕", code: "Pizza-Red-472" },
                { icon: "🍣", code: "Sushi-Purple-847" },
                { icon: "🥭", code: "Mango-Gold-213" },
                { icon: "🌮", code: "Taco-Blue-658" },
              ].map((c, i) => (
                <div key={i} className={styles['room-code-pill']}>
                  <span className={styles['rcp-icon']}>{c.icon}</span>
                  {c.code}
                  <span className={styles['rcp-math']}>example</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* CHALLENGES */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(20)}>
            <div className={styles['sec-chip']}>Challenges & Solutions</div>
            <h2 className={styles['sec-heading']}>Seven bugs that <span className={styles.a2}>taught me everything</span></h2>
            <p className={styles['sec-sub']}>Real-time multiplayer in vanilla JS surfaces edge cases fast. Each challenge below changed how I think about shared state.</p>
          </div>
          <div className={styles['ch-grid']}>
            {CHALLENGES.map((c, i) => (
              <div key={i} className={`${styles['ch-card']} ${styles.reveal}`} ref={r(21 + i)} style={{ transitionDelay: `${i * 0.07}s` }}>
                <div className={styles['ch-head']}>
                  <span className={styles['ch-num']}>{c.num}</span>
                  <div>
                    <div className={styles['ch-problem-lbl']}>Problem</div>
                    <div className={styles['ch-problem']}>{c.problem}</div>
                  </div>
                </div>
                <div className={styles['ch-body']}>
                  <div className={styles['ch-fix-lbl']}>Fix</div>
                  <p className={styles['ch-fix']}>{c.fix}</p>
                  <pre className={styles['ch-code']}>{c.code}</pre>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(32)}>
            <div className={styles['sec-chip']}>Key Features</div>
            <h2 className={styles['sec-heading']}>What makes it <span className={styles.a1}>actually work</span></h2>
            <p className={styles['sec-sub']}>The features that elevate this beyond a basic demo — all implemented in vanilla JS without a single npm package.</p>
          </div>
          <div className={styles['feat-grid']}>
            {FEATURES.map((f, i) => (
              <div key={i} className={`${styles['feat-card']} ${styles[f.cls]} ${styles.reveal}`} ref={r(33 + i)} style={{ transitionDelay: `${i * 0.07}s` }}>
                <span className={styles['feat-icon']}>{f.icon}</span>
                <div className={styles['feat-title']}>{f.title}</div>
                <p className={styles['feat-desc']}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SCREENSHOTS MASONRY ══ */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={`${styles['ss-section-header']} ${styles.reveal}`} ref={r(50)}>
            <div className={styles['sec-chip']}>UI Screenshots</div>
            <h2 className={styles['sec-heading']}>All <span className={styles.a1}>8 pages</span> visualised</h2>
            <p className={styles['sec-sub']}>Designed mockups of every screen — from landing to winner. Hover each card to see page details. Replace with real screenshots when ready.</p>
            <div className={styles['ss-count-row']}>
              <span className={styles['ss-count-chip']}>⬡ 8 HTML pages</span>
              <span className={styles['ss-count-chip']}>⬡ 3-column masonry</span>
              <span className={styles['ss-swap-note']}><span className={styles['ss-swap-dot']}/>swap any card with a real &lt;img&gt; when screenshots are ready</span>
            </div>
          </div>

          <div className={`${styles['masonry-grid']} ${styles.reveal}`} ref={r(51)}>


            {/* ─── 2 · CREATE ROOM ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-lime']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgLobby} alt="Create Room" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']} style={{color:"var(--neon3)",textShadow:"0 0 14px rgba(170,255,0,0.8)"}}>CREATE ROOM</div>
                <div className={styles['ss-ov-file']}>create-game.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

            {/* ─── 3 · COUNTDOWN (tall, dramatic) ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-cyan']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgCountdown} alt="Countdown" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']}>COUNTDOWN</div>
                <div className={styles['ss-ov-file']}>countdown.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

            {/* ─── 4 · JOIN ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-pink']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgGame} alt="Join Room" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']} style={{color:"var(--neon2)",textShadow:"0 0 14px rgba(255,0,110,0.8)"}}>JOIN ROOM</div>
                <div className={styles['ss-ov-file']}>join.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

            {/* ─── 5 · LOBBY ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-lime']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgLeaderboard} alt="Lobby" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']} style={{color:"var(--neon3)",textShadow:"0 0 14px rgba(170,255,0,0.8)"}}>LOBBY</div>
                <div className={styles['ss-ov-file']}>player-home.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

            {/* ─── 6 · GAME PAGE (tallest) ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-cyan']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgGamePage} alt="Game Page" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']}>GAME PAGE</div>
                <div className={styles['ss-ov-file']}>game-page.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

            {/* ─── 7 · SCORING ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-violet']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgScoring} alt="Peer Scoring" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']} style={{color:"var(--neon5)",textShadow:"0 0 14px rgba(191,0,255,0.8)"}}>PEER SCORING</div>
                <div className={styles['ss-ov-file']}>scoring.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

            {/* ─── 8 · WINNER (tall, dramatic) ─── */}
            <div className={`${styles['ss-item']} ${styles['ac-amber']}`}>
              <div className={styles['ss-chrome']}>
                <div className={`${styles['ss-dot']} ${styles.r}`}/><div className={`${styles['ss-dot']} ${styles.y}`}/><div className={`${styles['ss-dot']} ${styles.g}`}/>
                
              </div>
              <img src={imgWinner} alt="Winner Screen" className={styles['ss-img']} />
              <div className={styles['ss-overlay']}>
                <div className={styles['ss-ov-name']} style={{color:"var(--neon4)",textShadow:"0 0 14px rgba(255,149,0,0.8)"}}>WINNER SCREEN</div>
                <div className={styles['ss-ov-file']}>winner.html</div>
                <div className={styles['ss-ov-badge']}>📸 replace with screenshot</div>
              </div>
            </div>

          </div>
        </div>
      </section>

     

      {/* FOOTER */}
     
      <Footer />
    </>
  );
}